import ConfigInterface from '@/interfaces/ConfigInterface';
import { Buffer } from 'buffer';

export function formatNumber(number: unknown, digits: number | null, digitsOptional = false): string {
  const floatNumber = parseFloat(`${number || 0}`);

  let numDigits = digits || 0;
  if (digitsOptional && Math.round(floatNumber) === floatNumber) {
    numDigits = 0;
  }

  return floatNumber
    .toLocaleString(navigator.language, {
      maximumFractionDigits: numDigits,
      minimumFractionDigits: numDigits,
    });
}

export function getUcConsentStatus(key: string): boolean | undefined {
  if (window.getUcConsentStatus) return window.getUcConsentStatus(key);

  return undefined;
}

export function getUcIdByKey(key: string): string | undefined {
  return window.UC_UI?.getSettingsLabels()?.services?.find((service: any) => service.name === key)?.id as string | undefined;
}

export function getUcDescriptionByKey(key: string): string | undefined {
  return window.UC_UI?.getSettingsLabels()?.services?.find((service: any) => service.name === key)?.description as string | undefined;
}

export function getUcLegalBasisByKey(key: string): string[] | undefined {
  return window.UC_UI?.getSettingsLabels()?.services?.find((service: any) => service.name === key)?.legalBasis as string[] | undefined;
}

export function acceptUcService(key: string): void {
  return window.UC_UI?.acceptService(getUcIdByKey(key) as string);
}

export function recVal<RecType = string>(obj: Array<any> | Record<any, any>, key: string, defaultVal: RecType | null = null): RecType | null {
  if (['string', 'number'].indexOf(typeof key) >= 0) {
    const keys = key.toString().split('.');
    let tmp = Array.isArray(obj) ? obj : { ...obj };
    let reachedLastKey = false;

    keys.forEach((k, inx) => {
      if (tmp !== null) {
        const tmpObject = tmp as unknown as Record<string, any>;

        if (typeof tmpObject[k] !== 'undefined') {
          tmp = tmpObject[k];

          if ((inx + 1) === keys.length) {
            reachedLastKey = true;
          }
        }
      }
    });

    if (reachedLastKey) {
      return tmp as RecType;
    }
  }

  return defaultVal;
}

export function sumObjectValues<ReturnType>(objectArr: Record<string, any>[], key: string, fallback: ReturnType): ReturnType {
  if (!objectArr.length) return fallback;

  let returnValue: any;

  objectArr.forEach((object) => {
    const objectVal = object?.[key];

    if (!['string', 'number'].includes(typeof objectVal)) return;

    if (!returnValue) {
      if (typeof objectVal === 'string') returnValue = '';
      if (typeof objectVal === 'number') returnValue = 0;
    }

    returnValue += objectVal;
  });

  return (returnValue || fallback) as ReturnType;
}

export function anonymizeIban(iban: string): string {
  return `${iban.substring(0, 2)}**${iban.substring(4, 7)}${'*'.repeat(11)}${iban.slice(-4)}`;
}

export function ucFirst(inputString: string): string {
  return inputString.charAt(0)
    .toUpperCase() + inputString.slice(1);
}

export function objectToUtf8QueryString(params: Record<string, any>): string {
  const queryParams = [] as Array<string>;

  Object.keys(params).forEach((key) => {
    if ([null, ''].indexOf(params[key]) < 0) {
      queryParams.push(`${key}=${params[key]}`);
    }
  });

  return `${queryParams.length ? '?' : ''}${queryParams.join('&')}`;
}

export function getAppConfig<ReType = ConfigInterface | undefined>(key: string | undefined = undefined, fallback: ReType | undefined = undefined): ReType {
  const appConfig = window.ioappConfig || {} as ConfigInterface;

  if (key) {
    return recVal(appConfig, key, fallback) as ReType;
  }

  return appConfig as ReType;
}

export function env(key: string, defaultValue: any = null): unknown {
  const config = getAppConfig<ConfigInterface>();
  const environmentVariables = config.env || {};

  return environmentVariables[key] || defaultValue;
}

export function setCookie(name: string, value: string, exdays = 28): void {
  const d = new Date();
  d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
  const expires = `expires=${d.toUTCString()}`;
  const domain = document.domain.match(/[^.]*\.[^.]*$/)?.[0];

  document.cookie = `${name}=${value};${expires};path=/;${domain ? `domain=.${domain};` : ''}SameSite=Lax;Secure`;
}

export function getCookie(name: string): string {
  const $name = `${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf($name) === 0) {
      return c.substring($name.length, c.length);
    }
  }
  return '';
}

export function getRandomId(prefix: string | null = null): string {
  return `${prefix || ''}${Buffer.from(JSON.stringify(Math.random() * 999999999 - Math.random() * 99999999), 'utf-8').toString('base64')}`;
}

export function getSaveImageUrl(url: any): string | undefined {
  let returnVal: string | undefined = url;
  const replaceURL = `${env('REPLACE_URL') || ''}`.trim();
  const shopURL = `${window.ioappConfig.shopUrl || ''}`.trim();

  if (replaceURL && shopURL && typeof returnVal === 'string') {
    returnVal = returnVal.replaceAll(replaceURL, shopURL) as string;
  }

  return returnVal;
}

export function canUseWebP(): boolean {
  const canvasElement = document.createElement('canvas');
  if (canvasElement.getContext && canvasElement.getContext('2d')) {
    // eslint-disable-next-line eqeqeq
    return canvasElement.toDataURL('image/webp').indexOf('data:image/webp') == 0;
  }

  return false;
}

const formatFunctions = {
  formatNumber,
  anonymizeIban,
  ucFirst,
  getAppConfig,
  env,
  setCookie,
  getRandomId,
  getCookie,
  getSaveImageUrl,
  canUseWebP,
};

export default formatFunctions;
